import { MAP_CONFIG } from '../config/constants'

/**
 * Calculate appropriate zoom level based on radius
 * @param {number} radiusKm - Radius in kilometers
 * @returns {number} - Appropriate zoom level
 */
export function getZoomLevel(radiusKm) {
  if (radiusKm <= 1) return 15
  if (radiusKm <= 2) return 15
  if (radiusKm <= 5) return 14
  if (radiusKm <= 10) return 12
  if (radiusKm <= 25) return 11
  return 11 // 50km
}

/**
 * Format distance from meters to kilometers
 * @param {number} distanceMeters - Distance in meters
 * @returns {string} - Formatted distance string (e.g., "5.2 km")
 */
export function formatDistance(distanceMeters) {
  if (!distanceMeters) return ''
  const km = (distanceMeters / 1000).toFixed(1)
  return `${km} km`
}

/**
 * Get default map center — reads ?lat=&lng= URL params first, then env vars.
 * @returns {Array} - [latitude, longitude]
 */
export function getDefaultMapCenter() {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search)
    const lat = parseFloat(params.get('lat'))
    const lng = parseFloat(params.get('lng'))
    if (!isNaN(lat) && !isNaN(lng)) return [lat, lng]
  }
  return [
    parseFloat(import.meta.env.VITE_DEFAULT_MAP_CENTER_LAT) || 39.7392,
    parseFloat(import.meta.env.VITE_DEFAULT_MAP_CENTER_LNG) || -104.9903
  ]
}

/**
 * Get initial zoom from ?zoom= URL param, falling back to DEFAULT_ZOOM.
 * @param {number|null} radiusKm - Optional: only pass when user has explicitly chosen a radius
 * @returns {number}
 */
export function getInitialZoom(radiusKm) {
  if (typeof window !== 'undefined') {
    const z = parseInt(new URLSearchParams(window.location.search).get('zoom'), 10)
    if (!isNaN(z) && z >= 1 && z <= 20) return z
  }
  // Only derive from radius when an explicit radius has been chosen by the user.
  // Ignore the live MapContext radius (which reflects current map bounds and
  // changes on every mount/resize) so the initial zoom stays stable even when
  // the MapContainer remounts due to a key change (e.g. immersive mode toggle).
  if (radiusKm == null) return MAP_CONFIG.DEFAULT_ZOOM
  return getZoomLevel(radiusKm)
}

/**
 * Check if a location is a US zip code
 * @param {string} location - Location string to check
 * @returns {boolean} - True if it's a 5-digit zip code
 */
export function isZipCode(location) {
  return /^\d{5}$/.test(location.trim())
}

/**
 * Calculate search radius from visible map bounds.
 * Returns the distance (km) from the map center to the NE corner.
 * @param {L.Map} map - Leaflet map instance
 * @returns {number} radius in kilometers (minimum 1, rounded up)
 */
export function getBoundsRadius(map) {
  try {
    const bounds = map.getBounds()
    const center = map.getCenter()
    const ne = bounds.getNorthEast()
    const radiusMeters = map.distance(center, ne)
    return Math.max(1, Math.ceil(radiusMeters / 1000))
  } catch {
    // Map pane not yet positioned (mid-animation or unmounted) — return null to skip
    return null
  }
}
